'use client'

import { ProjectNew } from '@/dc-shared-web/src'
import { Icon } from '@/dc-shared-web/src/components/base/Icon'
import { faFolder } from '@document-crunch/pro-regular-svg-icons'
import { ReactNode } from 'react'
import { usePathWithParams } from '@/app/core/hooks/usePathWithParams'
import { FolderActions } from '@/app/(dashboard)/folders/FolderActions'
import { useSearchParams } from 'next/navigation'
import { ListItemCard } from '@/app/(dashboard)/ListItemCard'
import {
  Folder,
  FoldersListItem,
} from '@/dc-shared-web/src/hooks/api/folder/types'
import { useGetFolders } from '@/dc-shared-web/src/hooks/api/folder/useGetFolderForMove'
import { LocationType } from '@/app/(dashboard)/projects/FolderSelectionTree'

const FoldersLayout = ({ children }: { children: ReactNode }) => (
  <div>
    <h2 className='tw-text-md tw-font-medium'>Folders</h2>
    <div
      data-testid='folders-list'
      className='tw-mt-4 tw-grid tw-gap-3 tw-grid-cols-[repeat(auto-fill,minmax(220px,1fr))]'
    >
      {children}
    </div>
  </div>
)

export const FoldersList = ({
  shouldShowActions,
  teamUuid,
  page = 'dashboard',
  parentFolder,
  parentProject,
}: {
  shouldShowActions: boolean
  teamUuid: string
  page: 'folder' | 'project' | 'dashboard'
  parentFolder?: Folder
  parentProject?: ProjectNew
}) => {
  const { data: folders, isLoading: isLoadingFolders } = useGetFolders({
    currentTeamUuid: teamUuid,
    folderUuid: parentFolder?.uuid,
    projectUuid: parentProject?.uuid,
  })

  const getInitialLocation = (): LocationType => {
    if (page === 'folder') {
      return {
        type: 'folder',
        id: parentFolder?.uuid || '',
        name: parentFolder?.name || '',
      }
    }

    if (page === 'project') {
      return {
        type: 'project',
        id: parentProject?.uuid || '',
        name: parentProject?.name || '',
      }
    }

    return {
      type: 'dashboard',
      id: 'dashboard',
      name: 'Dashboard',
    }
  }
  // If there are no folders, don't render anything.
  if (!folders || folders.length === 0 || isLoadingFolders) {
    return null
  }

  return (
    <FoldersLayout>
      {folders?.map((folder, index) => (
        <FolderCard
          key={index}
          teamUuid={teamUuid}
          folder={folder}
          shouldShowActions={shouldShowActions}
          initialLocation={getInitialLocation()}
        />
      ))}
    </FoldersLayout>
  )
}

const FolderCard = ({
  folder,
  teamUuid,
  shouldShowActions,
  initialLocation,
}: {
  folder: FoldersListItem
  teamUuid: string
  shouldShowActions: boolean
  initialLocation: LocationType
}) => {
  const folderDetailsPath = usePathWithParams(`/folders/${folder.uuid}`)
  const isNew = useSearchParams().get('newFolderId') === folder.uuid

  return (
    <ListItemCard
      data-testid='folder'
      href={folderDetailsPath}
      title={folder.name}
      icon={<Icon icon={faFolder} size='1x' className='tw-text-foreground' />}
      rightSlot={
        <FolderActions
          folder={folder}
          teamUuid={teamUuid}
          shouldShowActions={shouldShowActions}
          initialLocation={initialLocation}
        />
      }
      isNew={isNew}
    />
  )
}
